import React, { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Form,
  ListGroup,
  Button,
  DropdownButton,
  Dropdown
} from "react-bootstrap"
import { toast } from "react-toastify"
import { HiOutlineMenu, HiOutlineRefresh, HiPencil, HiTrash, HiLockClosed } from "react-icons/hi"
import { SortableContainer, SortableElement } from "react-sortable-hoc"
import { Formik } from "formik"
import { string, object } from "yup"
import { withTranslation } from "react-i18next"

import { updateGuide, addImageToGuide, addVideoToGuide } from "../../../../actions/guides"
import IonIcon from "../../../../components/ionIcon"
import FileUploader from "../../../../components/fileUploader"
import ConfirmationModal from "../../../../components/confirmation"
import { getLocaleValue } from "../../../../helpers/getLocaleValue"
import { reorderArray } from "../../../../helpers/reorderArray"
import { GuideText, GuideImage, GuideVideo, GuideVideoThumbnail, GuideModal } from "../components"
import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from "../../../../constants/fileHandling"
import { GuideTextList } from "../components/text"

// const validationSchema = t => {
//   return object({
//     fi: string(),
//     en: string()
//     // fi: object({ text: string(), level: string().optional() }),
//     // en: object({ text: string(), level: string().optional() })
//   })
// }

const SortableItem = SortableElement(({ data, type, language, onClick, selected }) => (
  <ListGroup.Item className={selected ? "selected" : ""} onClick={onClick}>
    <div className="round-shadow-btn">
      <HiOutlineMenu />
    </div>
    {(() => {
      switch (type) {
        case "header":
        case "paragraph":
          return <GuideTextList data={data} language={language} type={type} />
        case "image":
          return <GuideImage data={data} list />
        case "video":
          return <GuideVideoThumbnail data={data} list />
        default:
          return null
      }
    })()}
  </ListGroup.Item>
))

const SortableList = SortableContainer(({ blocks, language, onClick, selectedContentIndex }) => {
  return (
    <div>
      {blocks.map(({ data, type }, index) => (
        <SortableItem
          key={`item-${index}`}
          index={index}
          sortIndex={index}
          data={data}
          type={type}
          language={language}
          onClick={() => onClick(index, { data, type })}
          selected={index === selectedContentIndex}
        />
      ))}
    </div>
  )
})

class EditGuideContainer extends Component {
  constructor(props) {
    super(props)
    const { id } = this.props.match.params
    this.formRef = React.createRef()

    this.state = {
      language: props.i18n.resolvedLanguage ?? "en",
      selectedGuideId: id,
      showEditTitleModal: false,
      confirmation: {
        show: false,
        callback: null,
        title: "",
        content: "",
        danger: false
      }
    }
  }

  showToast = (type, message, values) => {
    const { t } = this.props
    const translatedMessage = t("guides.notifications." + message, values)
    if (type === "success") {
      toast.success(translatedMessage)
    } else if (type === "error") {
      toast.error(translatedMessage)
    }
  }

  sortEnd = (oldIndex, newIndex) => {
    const { selectedGuideId, selectedContentIndex } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (!guide?.id) {
      this.showToast("error", "sort.failed")
      return
    }

    const blocks = [...guide.content.blocks]
    const [moved] = blocks.splice(oldIndex, 1)
    blocks.splice(newIndex, 0, moved)

    const updatedGuide = { ...guide, content: { ...guide.content, blocks } }

    this.props
      .updateGuide(guide.id, updatedGuide)
      .then(() => {
        let newSelected = selectedContentIndex
        if (selectedContentIndex === oldIndex) {
          newSelected = newIndex
        }
        this.setState({ selectedContentIndex: newSelected })
      })
      .catch(() => this.showToast("error", "sort.failed"))
  }

  onSortEnd = (form, oldIndex, newIndex) => {
    if (oldIndex === newIndex) return

    if (!form || !form.dirty) {
      this.sortEnd(oldIndex, newIndex)
    } else {
      this.setState({
        confirmation: {
          show: true,
          callback: () => {
            this.hideConfirmation()
            this.sortEnd(oldIndex, newIndex)
          },
          title: "dirty",
          content: "dirty"
        }
      })
    }
  }

  selectContent = (index, { data, type }) => {
    this.setState({ selectedContentIndex: index, selectedContent: data, selectedType: type })
  }

  onSelectContent = (form, index, block) => {
    if (!form || !form.dirty) {
      this.selectContent(index, block)
    } else {
      this.setState({
        confirmation: {
          show: true,
          callback: () => {
            this.hideConfirmation()
            this.selectContent(index, block)
          },
          title: "dirty",
          content: "dirty"
        }
      })
    }
  }

  onSaveContent = (values, resetForm) => {
    const { selectedGuideId, selectedContentIndex, selectedType } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (!guide) {
      this.showToast("error", "updateContent.failed")
      return
    }

    const updatedGuide = {
      ...guide,
      content: {
        ...guide.content,
        blocks: guide.content.blocks.map((block, idx) =>
          idx === selectedContentIndex ? { data: values, type: selectedType } : block
        )
      }
    }

    this.props
      .updateGuide(guide.id, updatedGuide)
      .then(() => this.showToast("success", "updateContent.success"))
      .then(() => resetForm({ values }))
      .catch(() => this.showToast("error", "updateContent.failed"))
  }

  onEditGuide = values => {
    this.setState({ showEditTitleModal: false })

    const { selectedGuideId } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (guide && guide.id) {
      // Make copy of current guide
      const updatedGuide = Object.assign({}, guide)
      // Edit guide values
      updatedGuide["title"] = values["titleEn"] || values["titleFi"]
      updatedGuide["titleEn"] = values["titleEn"]
      updatedGuide["titleFi"] = values["titleFi"]
      updatedGuide["icon"] = values["icon"]
      updatedGuide["isDraft"] = values["isDraft"]
      // Save changes
      this.props
        .updateGuide(guide.id, updatedGuide)
        .then(() => this.showToast("success", "updateGuide.success"))
        .catch(e => this.showToast("error", "updateGuide.failed"))
    } else {
      this.showToast("error", "updateGuide.failed")
    }
  }

  addTextContent = selectedType => {
    const { selectedGuideId } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (!guide) {
      this.showToast("error", "updateContent.failed")
      return
    }

    const emptyContent = {
      data: {
        en: {
          text: "",
          level: 2
        },
        fi: {
          text: "",
          level: 2
        }
      },
      type: selectedType
    }

    const updatedGuide = {
      ...guide,
      content: {
        ...guide.content,
        blocks: [...guide.content.blocks, emptyContent]
      }
    }

    this.props
      .updateGuide(guide.id, updatedGuide)
      .then(response => response.payload)
      .then(response => {
        const index = response.content.blocks.length - 1
        const selectedContent = response.content.blocks[index]
        this.selectContent(index, selectedContent)
      })
      .then(() => this.showToast("success", "addTextContent.success"))
      .catch(e => this.showToast("error", "addTextContent.failed"))
  }

  onAddTextContent = (form, type) => {
    if (!form || !form.dirty) {
      this.addTextContent(type)
    } else {
      this.setState({
        confirmation: {
          show: true,
          callback: () => {
            this.hideConfirmation()
            this.addTextContent(type)
          },
          title: "dirty",
          content: "dirty"
        }
      })
    }
  }

  onAddImageContent = async images => {
    const { selectedGuideId } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (guide && guide.id) {
      this.props
        .addImageToGuide(images, guide.id)
        .then(() => this.showToast("success", "addImageContent.success"))
        .catch(e => this.showToast("error", "addImageContent.failed"))
    } else {
      this.showToast("error", "addImageContent.failed")
    }
  }

  onAddVideoContent = async videos => {
    const { selectedGuideId } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (guide && guide.id && videos) {
      this.props
        .addVideoToGuide(videos, guide.id)
        .then(() => this.showToast("success", "addVideoContent.success"))
        .catch(e => this.showToast("error", "addVideoContent.failed.default"))
      // }
    } else {
      this.showToast("error", "addVideoContent.failed.default")
    }
  }

  openTitleEditModal = () => {
    this.setState({ showEditTitleModal: true })
  }

  closeTitleEditModal = () => {
    this.setState({ showEditTitleModal: false })
  }

  deleteContent = () => {
    const { selectedGuideId, selectedContentIndex } = this.state
    const guide = this.props.destination.guides.find(x => x.id === selectedGuideId)

    if (guide && guide.id && selectedContentIndex !== null) {
      const updatedContent = {
        ...guide.content,
        blocks: guide.content.blocks.filter((_, i) => i !== selectedContentIndex)
      }

      // Save changes
      this.props
        .updateGuide(guide.id, { content: updatedContent })
        .then(() => this.showToast("success", "deleteContent.success"))
        .then(() =>
          this.setState({
            selectedContentIndex: null,
            selectedContent: null,
            selectedType: null
          })
        )
        .catch(e => this.showToast("error", "deleteContent.failed"))
    } else {
      this.showToast("error", "deleteContent.failed")
    }
  }

  onDeleteContent = () => {
    this.setState({
      confirmation: {
        show: true,
        callback: () => {
          this.hideConfirmation()
          this.deleteContent()
        },
        title: "delete",
        content: "delete.content",
        danger: true
      }
    })
  }

  hideConfirmation = () => {
    this.setState({
      confirmation: {
        show: false,
        callback: null,
        title: "",
        content: "",
        danger: false
      }
    })
  }

  render() {
    const { language, selectedGuideId, selectedContentIndex, selectedContent, selectedType } = this.state
    const { t, destination, isLoading } = this.props
    if (!destination) return null // Fixup, prevent crash after logout

    const guide = destination.guides.find(x => x.id === selectedGuideId) // Selected guide

    return (
      <Container fluid className="mb-2">
        <GuideModal
          show={this.state.showEditTitleModal}
          data={guide}
          onSubmit={this.onEditGuide}
          onCancel={this.closeTitleEditModal}
        />

        <ConfirmationModal
          show={this.state.confirmation.show}
          title={this.state.confirmation.title}
          content={this.state.confirmation.content}
          onOK={this.state.confirmation.callback}
          onCancel={this.hideConfirmation}
          danger={this.state.confirmation.danger}
        />

        <Row className="tw-mb-4">
          <Col>
            <div className="tw-flex tw-items-center tw-justify-between">
              <h3 className="tw-font-semibold">{t("guides.title")}</h3>
              {guide?.isLocked && (
                <div
                  className="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-yellow-50 
                  tw-px-3 tw-py-1.5 tw-text-yellow-800 tw-shadow">
                  <HiLockClosed className="tw-mr-2 tw-h-4 tw-w-4 tw-text-yellow-700" />
                  <span>{t("guides.lockedNotice")}</span>
                </div>
              )}
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <Row>
                <Col xs={3}>
                  {guide && (
                    <ListGroup className="h-100 br-2">
                      <div className="p-3 guide-list-header">
                        <Row>
                          <Col xs={12}>
                            <Nav
                              justify
                              defaultActiveKey={language}
                              onSelect={language => this.setState({ language })}
                              className="mb-3 language-navigation">
                              <Nav.Item>
                                <Nav.Link eventKey="fi">{t("common.languages.fi")}</Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                <Nav.Link eventKey="en">{t("common.languages.en")}</Nav.Link>
                              </Nav.Item>
                            </Nav>
                          </Col>
                          <Col xs={12}>
                            <IonIcon name={guide.icon} />
                            <span className="ml-2 font-weight-bold">{getLocaleValue(guide, "title", language)}</span>
                            <Button
                              variant="link"
                              className="btn p-0 float-right text-primary"
                              onClick={this.openTitleEditModal}>
                              <HiPencil className="icon mr-1" />
                              <span className="small">{t("guides.button.edit")}</span>
                            </Button>
                          </Col>
                        </Row>
                      </div>
                      {isLoading && (
                        <div className="loading-spinner">
                          <HiOutlineRefresh className="slow-spin icon loading-spinner-icon" />
                        </div>
                      )}
                      <SortableList
                        blocks={guide.content?.blocks}
                        selectedContentIndex={selectedContentIndex}
                        language={language}
                        onSortEnd={({ oldIndex, newIndex }) => this.onSortEnd(this.formRef.current, oldIndex, newIndex)}
                        distance={5}
                        onClick={(index, block) => this.onSelectContent(this.formRef.current, index, block)}
                      />
                      {guide?.content?.blocks && !guide.content.blocks.length && (
                        <ListGroup.Item>
                          <p className="text-muted text-center font-italic m-0 p-4">
                            {t("guides.selectedGuide.noContent")}
                          </p>
                        </ListGroup.Item>
                      )}
                      <ListGroup.Item className="h-100 d-flex flex-column justify-content-end">
                        <DropdownButton
                          title={t("guides.button.new")}
                          id="add-guide-content-dropdown"
                          className="text-center m-2 dropdown-btn-block">
                          <Dropdown.Item onClick={() => this.onAddTextContent(this.formRef.current, "header")}>
                            {t("guides.button.dropdown.header")}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => this.onAddTextContent(this.formRef.current, "paragraph")}>
                            {t("guides.button.dropdown.paragraph")}
                          </Dropdown.Item>
                          <FileUploader
                            handleFile={this.onAddImageContent}
                            accept="image/*,.heic"
                            tooltip={t("guides.tooltips.image", { max_size: MAX_IMAGE_FILE_SIZE })}>
                            <Dropdown.Item>{t("guides.button.dropdown.image")}</Dropdown.Item>
                          </FileUploader>
                          <FileUploader
                            handleFile={this.onAddVideoContent}
                            accept="video/*,.mkv,.3gp"
                            tooltip={t("guides.tooltips.video", { max_size: MAX_VIDEO_FILE_SIZE })}>
                            <Dropdown.Item bsPrefix="dropdown-item text-prewrap-xl">
                              {t("guides.button.dropdown.video")}
                            </Dropdown.Item>
                          </FileUploader>
                        </DropdownButton>
                      </ListGroup.Item>
                    </ListGroup>
                  )}
                </Col>
                <Col xs={9}>
                  <div className="py-4 pr-4">
                    <Row className="mb-3">
                      <Col>{selectedType && <h5>{t("guides.selectedContent.title." + selectedType)}</h5>}</Col>
                      <Col>
                        {selectedContentIndex >= 0 && (
                          <Button
                            variant="link"
                            className="float-right text-danger"
                            disabled={isLoading}
                            onClick={this.onDeleteContent}>
                            <HiTrash className="icon mr-1" />
                            {t("guides.button.delete")}
                          </Button>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {selectedContent && selectedType === "image" && <GuideImage data={selectedContent} full />}
                        {selectedContent && selectedType === "video" && <GuideVideo data={selectedContent} full />}
                        {selectedContent && (selectedType === "header" || selectedType === "paragraph") && (
                          <Formik
                            innerRef={this.formRef}
                            key={"Formik-" + selectedContentIndex}
                            enableReinitialize={true}
                            initialValues={selectedContent}
                            // validationSchema={validationSchema(t)}
                            validateOnBlur={false}
                            onSubmit={(values, { resetForm }) => this.onSaveContent(values, resetForm)}>
                            {({ handleSubmit, handleReset, handleChange, handleBlur, values, ...props }) => (
                              <Form noValidate onSubmit={handleSubmit}>
                                {selectedType === "header" && (
                                  <Row>
                                    <Form.Group as={Col} xs={12} xl={6} controlId="fi" className="position-relative">
                                      <Form.Text className="text-muted">{t("guides.form.titleFi")}</Form.Text>
                                      <Form.Control
                                        autoFocus
                                        type="text"
                                        name="fi.text"
                                        placeholder=""
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values?.fi["text"] ?? ""}
                                        isInvalid={props.touched.fi && props.errors.fi}
                                        className={props.touched.fi && props.errors.fi ? "has-error" : null}
                                      />
                                      <Form.Control.Feedback tooltip type="invalid">
                                        {props.errors.fi}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} xs={12} xl={6} controlId="en" className="position-relative">
                                      <Form.Text className="text-muted">{t("guides.form.titleEn")}</Form.Text>
                                      <Form.Control
                                        type="text"
                                        name="en.text"
                                        placeholder=""
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values?.en["text"] ?? ""}
                                        isInvalid={props.touched.en && props.errors.en}
                                        className={props.touched.en && props.errors.en ? "has-error" : null}
                                      />
                                      <Form.Control.Feedback tooltip type="invalid">
                                        {props.errors.en}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Row>
                                )}

                                {selectedType === "paragraph" && (
                                  <Row>
                                    <Form.Group as={Col} xs={12} xl={6} controlId="fi" className="position-relative">
                                      <Form.Text className="text-muted">{t("guides.form.paragraphFi")}</Form.Text>
                                      <Form.Control
                                        as="textarea"
                                        rows={6}
                                        name="fi.text"
                                        placeholder=""
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values?.fi["text"] ?? ""}
                                        isInvalid={props.touched.fi && props.errors.fi}
                                        className={props.touched.fi && props.errors.fi ? "has-error" : null}
                                        spellCheck="false"
                                      />
                                      <Form.Control.Feedback tooltip type="invalid">
                                        {props.errors.fi}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} xs={12} xl={6} controlId="en" className="position-relative">
                                      <Form.Text className="text-muted">{t("guides.form.paragraphEn")}</Form.Text>
                                      <Form.Control
                                        as="textarea"
                                        rows={6}
                                        name="en.text"
                                        placeholder=""
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values?.en["text"] ?? ""}
                                        isInvalid={props.touched.en && props.errors.en}
                                        className={props.touched.en && props.errors.en ? "has-error" : null}
                                        spellCheck="false"
                                      />
                                      <Form.Control.Feedback tooltip type="invalid">
                                        {props.errors.en}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Row>
                                )}
                                {props.dirty && (
                                  <div className="fixed-footer-bar">
                                    <div className="float-left">
                                      <span className="text-dark align-middle">
                                        {t("common.unsavedChanges.content")}
                                      </span>
                                    </div>
                                    <div className="float-right">
                                      <Button
                                        variant="link"
                                        onClick={handleReset}
                                        className="mr-4"
                                        type="reset"
                                        disabled={isLoading}>
                                        {t("common.unsavedChanges.button.cancel")}
                                      </Button>
                                      <Button
                                        variant="primary"
                                        className="btn-default-lg"
                                        type="submit"
                                        disabled={isLoading}>
                                        {t("common.unsavedChanges.button.save")}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </Form>
                            )}
                          </Formik>
                        )}
                        {!selectedContent && (
                          <p className="text-muted font-italic mt-5 text-center">
                            {t("guides.selectedContent.unselected")}
                          </p>
                        )}
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
    )
  }
}

EditGuideContainer.propTypes = {
  isLoading: PropTypes.bool
}

EditGuideContainer.defaultProps = {
  isLoading: false
}

const mapStateToProps = state => ({
  isLoading: state.destinations.isLoading,
  destination: state.destinations.destinations.find(x => x.id === state.destinations.selectedDestinationId)
})

const mapDispatchToProps = dispatch => ({
  updateGuide: (id, data) => dispatch(updateGuide(id, data)),
  addImageToGuide: (image, id) => dispatch(addImageToGuide(image, id)),
  addVideoToGuide: (video, thumbnail, id) => dispatch(addVideoToGuide(video, thumbnail, id))
})

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(EditGuideContainer))
